//-------------------------------------------------------------------------------------------------- Imports
import { Button, Card, Col, Divider, Row, Space, Typography } from "antd";
import { StyleSheet } from "../../../StyleSheet";
import CardRecharges from "./CardRecharges";
import { useCallback, useEffect, useState } from "react";
import { doRecharge, getRecharges } from "../../../methods/Methods";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../stores/store";
import { clearAlert, setAlert, showAlert } from "../../../stores/slices/alertSlice";
import Loading from "../../../components/Loading";
import _ from "lodash";
import InfoCardComponent from "../../../components/InfoCardComponent";
import { Paquetes, RequestRecharge } from "../../../types/common";
import RechargeWithCard from "./RechargeWithCard";
import { CardRechargeProps } from "./PaymentCard";
import { ContractInterface } from "../../../stores/slices/generalDataSlice";
const { Text } = Typography;

//-------------------------------------------------------------------------------------------------- Interface
interface MobileRechargesProps {
    clientData: ContractInterface | null;
    phone: string;
    setViewToRender: Function
};

//-------------------------------------------------------------------------------------------------- View
const MobileRecharges = (props: MobileRechargesProps) => {
    const { clientData, phone, setViewToRender } = props;
    const dispatch = useDispatch<AppDispatch>();
    const [additionalCardInfo, setAdditionalCardInfo] = useState<JSX.Element | null>(null);
    const formatter = Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" });
    // Control
    const [isLoading, setLoading] = useState<boolean>(true);
    const [toPayment, setToPaymet] = useState<boolean>(false);
    // Data
    const [packages, setPackages] = useState<Paquetes[]>([]);
    const [clientId, setClientId] = useState<string>("");
    const [selectedPackage, setSelectedPackage] = useState<Paquetes>();

    const handleFinalize = useCallback(() => {
        dispatch(showAlert(false));
        dispatch(clearAlert);
    }, [dispatch]);

    const functionRecharge = useCallback(async (cardData: CardRechargeProps) => {
        if (selectedPackage && clientData && phone) {
            setLoading(true);

            let error = false;

            const request: RequestRecharge = {
                sucursal: clientData.sucursal,
                suscriptor: clientData.suscriptor,
                name: cardData.name,
                tarjeta: cardData.number,
                expmonth: cardData.expirationDateCard.substring(0, 2),
                expyear: cardData.expirationDateCard.substring(2, 4),
                cvv_csc: cardData.cvvCard,
                id_paquete: selectedPackage.Id_paquete,
                numero: phone
            };
            await doRecharge(clientData.sucursal + clientData.suscriptor, request)
                .then((response) => {
                    if (response.data[0].estatus.toUpperCase() === "ERROR") {
                        error = true;
                    }
                }).catch(() => {
                    error = true;
                });

            setLoading(false);


            if (error) {
                dispatch(setAlert({
                    type: "ERROR",
                    title: "¡Estimado usuario!",
                    message: "Ocurrió un error al realizar la recarga.\nIntente de nuevo más tarde.\nPara cualquier aclaración, comuníquese a servicio al cliente.",
                    show: true,
                    onFinalize: handleFinalize
                }));
            }
            else {
                setViewToRender(0);
                dispatch(setAlert({
                    type: "SUCCESS",
                    title: "¡Estimado usuario!",
                    subtitle: "Su recarga fue realizada con éxito.",
                    message: `
                        Se ha activado su paquete en su linea Megamovil, incluye:\n
                        ${selectedPackage.datos} MB de internet para navegar.\n
                        ${selectedPackage.minutos} minutos para llamadas.\n
                        ${selectedPackage.sms} SMS
                        ${(selectedPackage.vigencia > 0) && (
                            "\nCon vigencia de " + selectedPackage.vigencia + (selectedPackage.vigencia === 1) ? (" día.") : (" días.")
                        )}
                    `,
                    show: true,
                    onFinalize: handleFinalize
                }));
            }
        }
    }, [selectedPackage, clientData, phone, dispatch, handleFinalize, setViewToRender]);

    // TODO: Considerar el recargar = 1 para cuando si pueda realizar recargas.
    const getRechargesFromApi = useCallback(async (idCliente: string) => {
        setLoading(true);

        let message = "";
        let success = false;

        await getRecharges(phone, idCliente).then((response) => {
            if (response.data) {
                setPackages(response.data[0].paquetes);
            }

            success = true;
        }).catch(() => {
            message = "No fue posible obtener la información en este momento.\nPor favor intente de nuevo más tarde."
        });

        if (!success) {
            dispatch(setAlert({
                type: "ERROR",
                show: true,
                title: "¡Estimado usuario!",
                message: message,
                onFinalize: handleFinalize
            }));
        }

        setLoading(false);
    }, [phone, handleFinalize, dispatch]);

    const handlePayRecharge = (selectedrecharge: Paquetes) => {
        setSelectedPackage(selectedrecharge)
        setToPaymet(true);
    };

    const formatMonto = useCallback((monto: string) => {
        const montoNumerico = parseFloat(monto);

        // Verificar si es un número válido
        if (isNaN(montoNumerico)) {
            return monto;
        }

        // Formatear el número
        return formatter.format(montoNumerico);
    }, [formatter]);

    const addAdditionalCard = useCallback((selectedRecharge: Paquetes) => {
        setSelectedPackage(selectedRecharge);
        if (clientId && selectedRecharge) {
            const updatedCardInfo = (
                <Card style={styles.cardStyle} bodyStyle={styles.bodyCard}>
                    <Row style={{ padding: '24px 24px 0px 24px' }}>
                        <Col span={24} style={{ textAlign: 'center' }}>
                            <div>
                                <Text style={styles.textPrincipal}>
                                    {formatMonto(selectedRecharge.monto)}
                                </Text>
                            </div>
                        </Col>
                    </Row>
                    <Row style={styles.rowDetail}>
                        <div style={styles.divDivider}>
                            <Divider style={styles.lineDivider} />
                        </div>
                        <div style={styles.divDetail}>
                            <Text style={styles.textDetail}>Datos para Navegar</Text>
                            <Text>{selectedRecharge.datos}</Text>
                        </div>
                        <div style={styles.divDivider}>
                            <Divider style={styles.lineDivider} />
                        </div>
                        <div style={styles.divDetail}>
                            <Text style={styles.textDetail}>Llamadas</Text>
                            <Text>{`${selectedRecharge.minutos} Minutos`}</Text>
                        </div>
                        <div style={styles.divDivider}>
                            <Divider style={styles.lineDivider} />
                        </div>
                        <div style={styles.divDetail}>
                            <Text style={styles.textDetail}>SMS</Text>
                            <Text>{selectedRecharge.sms}</Text>
                        </div>
                        <div style={styles.divDivider}>
                            <Divider style={styles.lineDivider} />
                        </div>
                    </Row>
                    <Row style={styles.rowDetail}>
                        <div>
                            <Button
                                style={styles.buttonFinal}
                                type="primary"
                                onClick={() => handlePayRecharge(selectedRecharge)}
                            >
                                Pagar
                            </Button>
                        </div>
                    </Row>
                </Card>
            );

            setAdditionalCardInfo(updatedCardInfo);
        }
    }, [clientId, formatMonto]);

    useEffect(() => {
        if (clientData) {
            setClientId(clientData.sucursal + clientData.suscriptor);
        }
    }, [clientData]);

    useEffect(() => {
        if (clientId !== "") {
            getRechargesFromApi(clientId);
        }
    }, [clientId, getRechargesFromApi]);

    return (
        (isLoading) ? (
            <Row style={{ height: "100%" }}>
                <Loading />
            </Row>
        ) : ((_.isEmpty(packages)) ? (
            <InfoCardComponent title="¡Estimado usuario!" description="En este momento no cuentas con paquetes de recarga disponibles." />
        ) : (
            <div style={{ marginBottom: 50 }}>
                {/* <Row>
                    <Col span={24}>
                        <SocialNetworking />
                    </Col>
                </Row> */}
                {(toPayment && selectedPackage) ? (
                    <RechargeWithCard pkg={selectedPackage} doRecharge={functionRecharge} />
                ) : (
                    <Row gutter={[16, 16]}>
                        <Col sm={12} xl={15}>
                            <Row gutter={[16, 16]}>
                                {packages.map((pkg, index) => (
                                    <Col sm={24} xl={12} xxl={8} key={index} style={styles.divRoot}>
                                        <Space direction="vertical">
                                            <CardRecharges
                                                selected={selectedPackage?.Id_paquete === pkg.Id_paquete}
                                                paquete={pkg}
                                                onClick={addAdditionalCard} />
                                        </Space>
                                    </Col>
                                ))}
                            </Row>
                        </Col>
                        <Col sm={12} xl={9} style={{ ...styles.divRoot }}>
                            {additionalCardInfo}
                        </Col>
                    </Row>
                )}
            </div>
        ))
    );
};



//-------------------------------------------------------------------------------------------------- Styles
const styles = StyleSheet.create({
    divRoot: {
        display: "flex",
        flexDirection: "column",
        marginTop: 20
    },
    cardStyle: {
        boxShadow: "0px 0px 8px #00000029",
        borderRadius: 12,
    },
    bodyCard: {
        width: "100%",
        padding: 0
    },
    textPrincipal: {
        fontSize: 40,
        color: '#0055c2',
        fontWeight: 600
    },
    divDays: {
        backgroundColor: '#0055c2',
        width: '100%',
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    divDetail: {
        background: 'white',
        textAlign: 'center',
        width: '100%',
        height: 40,
        display: 'flex',
        flexDirection: "column",
        alignItems: 'center',
        justifyContent: 'center',
        margin: "10px 0px 10px 0px",
    },
    divDivider: {
        width: '90%',
        textAlign: 'center'
    },
    lineDivider: {
        backgroundColor: '#777e86',
        margin: "5px 0",
        width: "90%"
    },
    rowDetail: {
        marginBottom: 20,
        alignItems: 'center',
        justifyContent: 'center'
    },
    textDetail: {
        color: '#777e86',
        fontWeight: 600
    },
    buttonFinal: {
        textAlign: "center",
        fontSize: 14,
        fontWeight: 600,
        margin: 10,
        borderRadius: 20,
        border: "none",
        color: "#ffffff",
        backgroundColor: "#0055c2",
    },
});



//-------------------------------------------------------------------------------------------------- Exports
export default MobileRecharges;
